// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InvoiceDetail } from '@/billing/api/types';
import { InvoiceEditFormBillToSection } from '@/billing/components/invoice-edit-form-bill-to-section';
import {
  InvoiceEditFormData,
  invoiceEditFormSchema,
} from '@/billing/schemas/invoice-edit-form-schema';
import { isCustomPaymentTerm } from '@/billing/utilities/custom-payment-term';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormTextField } from '@/components/form-text-field';
import { SyncOutlined } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import {
  flourishFieldSx,
  useLinkedFieldFlourish,
} from '@/hooks/use-linked-field-flourish';
import { PaymentTerm } from '@/payment-term/api/types';
import { PaymentTermAutocomplete } from '@/payment-term/components/payment-term-autocomplete';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface InvoiceEditFormProps {
  /**
   * The invoice to edit — used to populate default values.
   */
  invoice: InvoiceDetail;

  /**
   * The payment term currently persisted on the invoice, resolved from the
   * fetched payment-term list by the dialog. A "Custom" sentinel when the
   * invoice has a non-null label that does not match any known payment
   * term. Null only when the invoice has no payment term set.
   */
  initialPaymentTerm: PaymentTerm | null;

  /**
   * The customer currently assigned to the invoice, resolved from the
   * fetched customer list by the dialog. Null when the invoice has no
   * customer.
   */
  initialCustomer: Customer | null;

  /**
   * The full customer list for the dropdown.
   */
  customers: Customer[];

  /**
   * The dropdown options. Includes the Custom sentinel as the first entry
   * when `initialPaymentTerm` is a sentinel.
   */
  paymentTermOptions: PaymentTerm[];

  /**
   * Callback fired when the form is submitted with valid data.
   * The dialog wrapper handles the API call and side effects.
   */
  onSubmit: (data: InvoiceEditFormData) => void;

  /**
   * The form ID used to connect the external submit button to this form.
   * The dialog wrapper provides this ID and uses it on its submit button.
   */
  formId: string;

  /**
   * If true, disables all inputs while a save is in flight.
   */
  isDisabled?: boolean;

  /**
   * Server-side error message to display on the invoice number field. Set
   * by the dialog when the API returns a UNIQUE_VIOLATION for the number.
   * Cleared when the user changes the field value.
   */
  invoiceNumberError?: string | null;

  /**
   * Whether the parent dialog is currently open. Used to reset the form
   * back to its derived defaults each time the dialog is reopened so the
   * user always starts from the latest invoice values.
   */
  isOpen: boolean;
}

function getDefaultValues(
  invoice: InvoiceDetail,
  initialPaymentTerm: PaymentTerm | null,
  initialCustomer: Customer | null
): InvoiceEditFormData {
  return {
    invoiceNumber: invoice.invoiceNumber,
    customer: initialCustomer,
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate,
    paymentTerm: initialPaymentTerm,
    billFromName: invoice.billFrom.name,
    billFromAddress: invoice.billFrom.address ?? '',
    billToCompanyName: invoice.billTo.companyName ?? '',
    billingContactName: invoice.billTo.billingContactName ?? '',
    billToAddress: invoice.billTo.address ?? '',
    billToPhone: invoice.billTo.phone ?? '',
    billToEmail: invoice.billTo.email ?? '',
    purchaseOrderNumber: invoice.purchaseOrderNumber ?? '',
  };
}

export function InvoiceEditForm(props: InvoiceEditFormProps) {
  const theme = useTheme();
  const flourish = useLinkedFieldFlourish();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
  } = useForm<InvoiceEditFormData>({
    resolver: zodResolver(invoiceEditFormSchema),
    defaultValues: getDefaultValues(
      props.invoice,
      props.initialPaymentTerm,
      props.initialCustomer
    ),
  });

  // Reset the form back to defaults each time the parent dialog transitions
  // from closed → open so the user always sees the latest invoice values
  // (and any prior in-flight edits are discarded).
  const wasOpenRef = useRef(props.isOpen);
  useEffect(() => {
    if (props.isOpen && !wasOpenRef.current) {
      reset(
        getDefaultValues(
          props.invoice,
          props.initialPaymentTerm,
          props.initialCustomer
        )
      );
    }
    wasOpenRef.current = props.isOpen;
  }, [
    props.isOpen,
    props.invoice,
    props.initialPaymentTerm,
    props.initialCustomer,
    reset,
  ]);

  // Map the server-side invoice number error into the form's field state
  // so it renders as inline helperText on the field.
  useEffect(() => {
    if (props.invoiceNumberError) {
      setError('invoiceNumber', {
        type: 'server',
        message: props.invoiceNumberError,
      });
    } else {
      clearErrors('invoiceNumber');
    }
  }, [props.invoiceNumberError, setError, clearErrors]);

  // When the payment term changes, recompute the due date from the current
  // invoice date plus the payment term's netDays. Triggers the linked-field
  // flourish on dueDate so the user notices the cascade. The user can still
  // override the due date manually afterward.
  //
  // The "Custom" sentinel is skipped: it has no meaningful netDays and the
  // due date was already set from whatever the invoice was storing.
  const handlePaymentTermChange = useCallback(
    (
      term: PaymentTerm | null,
      fieldOnChange: (value: PaymentTerm | null) => void
    ) => {
      fieldOnChange(term);
      if (term === null) return;
      if (isCustomPaymentTerm(term)) return;
      if (term.netDays === null) return;
      const invoiceDate = getValues('invoiceDate');
      if (!invoiceDate?.isValid) return;
      const nextDueDate = invoiceDate.plus({ days: term.netDays });
      setValue('dueDate', nextDueDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      flourish.trigger('dueDate');
    },
    [flourish, getValues, setValue]
  );

  // Watch date and payment term fields for cascade and mismatch detection.
  const watchedInvoiceDate = watch('invoiceDate');
  const watchedDueDate = watch('dueDate');
  const watchedPaymentTerm = watch('paymentTerm');

  // When invoice date changes, recalculate the due date from the current
  // payment term. Uses the FormDatePicker's onChange so the cascade happens
  // synchronously in the same event — no intermediate render with a stale
  // due date that would flicker the mismatch warning.
  const handleInvoiceDateChange = useCallback(
    (date: DateTime | null) => {
      if (!date || !date.isValid) return;
      const term = getValues('paymentTerm');
      if (!term) return;
      if (isCustomPaymentTerm(term)) return;
      if (term.netDays === null) return;
      const nextDueDate = date.plus({ days: term.netDays });
      setValue('dueDate', nextDueDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      flourish.trigger('dueDate');
    },
    [flourish, getValues, setValue]
  );

  const dueDateMismatch = useMemo(() => {
    if (!watchedPaymentTerm) return null;
    if (isCustomPaymentTerm(watchedPaymentTerm)) return null;
    if (watchedPaymentTerm.netDays === null) return null;
    if (!watchedInvoiceDate?.isValid) return null;
    if (!watchedDueDate?.isValid) return null;
    const calculatedDueDate = watchedInvoiceDate.plus({
      days: watchedPaymentTerm.netDays,
    });
    const diffDays = Math.round(
      watchedDueDate.diff(calculatedDueDate, 'days').days
    );
    if (diffDays === 0) return null;
    return {
      diffDays,
      calculatedDueDate,
      termLabel: watchedPaymentTerm.label,
    };
  }, [watchedInvoiceDate, watchedDueDate, watchedPaymentTerm]);

  const handleSyncDueDate = useCallback(() => {
    if (!dueDateMismatch) return;
    setValue('dueDate', dueDateMismatch.calculatedDueDate, {
      shouldDirty: true,
      shouldValidate: true,
    });
    flourish.trigger('dueDate');
  }, [dueDateMismatch, flourish, setValue]);

  const dueDateFlourishActive = flourish.isActive('dueDate');
  const dueDateSx = flourishFieldSx(theme, dueDateFlourishActive);

  return (
    <form id={props.formId} onSubmit={handleSubmit(props.onSubmit)} noValidate>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Col 1: invoice number + bill from */}
        <Stack spacing={3}>
          <Stack spacing={2}>
            <Typography variant="subtitle2">Invoice number</Typography>
            <FormTextField
              name="invoiceNumber"
              control={control}
              label="Invoice number"
              fullWidth
              disabled={props.isDisabled}
            />
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle2">Bill from</Typography>
            <FormTextField
              name="billFromName"
              control={control}
              label="Name"
              fullWidth
              disabled={props.isDisabled}
            />
            <FormTextField
              name="billFromAddress"
              control={control}
              label="Address"
              fullWidth
              isMultiline
              minimumRows={3}
              disabled={props.isDisabled}
            />
          </Stack>
        </Stack>

        {/* Col 2: bill to + billing contact (suspends on project billing info) */}
        <Suspense
          fallback={
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          }
        >
          <InvoiceEditFormBillToSection
            control={control}
            setValue={setValue}
            customers={props.customers}
            projectKey={props.invoice.project?.key ?? null}
            isDisabled={props.isDisabled}
          />
        </Suspense>

        {/* Col 3: invoice details */}
        <Stack spacing={2}>
          <Typography variant="subtitle2">Invoice details</Typography>
          <FormDatePicker
            name="invoiceDate"
            control={control}
            label="Invoice date"
            isDisabled={props.isDisabled}
            onChangeComplete={handleInvoiceDateChange}
          />
          <Controller
            name="paymentTerm"
            control={control}
            render={({ field, fieldState }) => (
              <PaymentTermAutocomplete
                size="small"
                label="Payment terms"
                options={props.paymentTermOptions}
                value={field.value}
                onChange={(_, value) =>
                  handlePaymentTermChange(value, field.onChange)
                }
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={props.isDisabled}
              />
            )}
          />
          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box
                sx={{
                  flex: 1,
                  ...dueDateSx,
                }}
              >
                <FormDatePicker
                  name="dueDate"
                  control={control}
                  label="Due date"
                  isDisabled={props.isDisabled}
                  isClearable
                />
              </Box>
              {dueDateMismatch && !dueDateFlourishActive && (
                <Tooltip title="Recalculate due date" arrow>
                  <IconButton
                    size="small"
                    onClick={handleSyncDueDate}
                    disabled={props.isDisabled}
                    sx={{ mt: 0.5 }}
                  >
                    <SyncOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {dueDateMismatch && !dueDateFlourishActive && (
              <Typography
                variant="caption"
                color="warning.dark"
                display="block"
                mt={0.5}
                pl={2}
              >
                Due date is{' '}
                {pluralize(Math.abs(dueDateMismatch.diffDays), {
                  singular: 'day',
                })}{' '}
                {dueDateMismatch.diffDays > 0 ? 'after' : 'before'} the{' '}
                {dueDateMismatch.termLabel} date of{' '}
                {dueDateMismatch.calculatedDueDate.toLocaleString({
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            )}
          </Box>
          <FormTextField
            name="purchaseOrderNumber"
            control={control}
            label="PO number"
            fullWidth
            disabled={props.isDisabled}
          />
        </Stack>
      </Box>
    </form>
  );
}
